import React, { Dispatch, SetStateAction } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Controller, FieldValues, UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { cachedSubTypesOptionsSelector } from '@europrocurement/l2d-domain';
import SelectField from '../../components/FormInputs/SelectField';
import TextField from '../../components/form/TextField';
import { ArticleAdditionalInformations, ArticlesSchema } from '../Form/validation';
import AdvertPricing from '../../components/AdvertPricing';
import { ArticleFormPricingState } from '../Form/types';
import QuantityModule from './QuantityModule';

type ComponentProps = {
    articlePath: string;
    context: UseFormReturn<FieldValues, unknown>;
    article: ArticlesSchema['articles'][number] & ArticleAdditionalInformations;
    isOptionForm?: boolean;
    setArticlePricingInformations: Dispatch<SetStateAction<ArticleFormPricingState>>;
    index: number;
};

const ArticleOverrideSubForm: React.FunctionComponent<ComponentProps> = function ({
    articlePath,
    context,
    article,
    isOptionForm,
    setArticlePricingInformations,
    index,
}) {
    const { subTypeId } = article;

    const sTypeOptions = useSelector(cachedSubTypesOptionsSelector);

    if (!sTypeOptions) {
        return <CircularProgress />;
    }

    const getInputName = (key: string) => `${articlePath}.${key}`;

    const updatePricingInformations = (informations: ArticleFormPricingState) => {
        setArticlePricingInformations(informations);
        const path = getInputName('priceInformations');
        context.setValue(path, informations);
    };

    const isAdvertCategory = article.originalArticle?.rubArticle === 'PUB';

    const {
        formState: { errors },
        control,
        setValue,
    } = context;

    const setQuantity = (quantity: number) => {
        setValue(`${articlePath}.'quantity'`, quantity);
    };

    const originalLabel = article.originalArticle?.libelle;

    return (
        <Box>
            {isAdvertCategory && (
                <>
                    <Controller
                        name={getInputName('subTypeId')}
                        control={control}
                        render={({ field }) => (
                            <SelectField
                                {...field}
                                required
                                label="Sous-type"
                                placeholder="Sélectionnez un sous-type"
                                name={field.name}
                                blurInputOnSelect
                                options={sTypeOptions}
                                errors={errors}
                            />
                        )}
                    />
                    <AdvertPricing
                        subTypeId={subTypeId ?? undefined}
                        setArticlePricingInformations={updatePricingInformations}
                    />
                </>
            )}
            <QuantityModule
                article={article}
                setQuantity={setQuantity}
                inputName={`articles.${index}.quantity`}
                errors={errors}
            />
            <Controller
                name={getInputName('label')}
                control={control}
                render={({ field }) => (
                    <TextField
                        sx={{ width: '100%' }}
                        {...field}
                        name={field.name}
                        placeholder={originalLabel ?? ''}
                        label="Libellé"
                        errors={errors}
                        required={false}
                    />
                )}
            />

            {isOptionForm && (
                <Controller
                    name={getInputName('regle')}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            sx={{ width: '100%' }}
                            {...field}
                            name={field.name}
                            placeholder="Règle"
                            label="Règle"
                            errors={errors}
                            required={false}
                        />
                    )}
                />
            )}
        </Box>
    );
};

export default ArticleOverrideSubForm;
